// Firebase Firestore imports for database operations
import { collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
// Firebase configuration and database instance
import { Firestore } from "../components/FirebaseAuth/firebase";
import { isKWALLAdmin } from "../utils/index";

/**
 * Organization Collection Service
 * 
 * This service provides centralized functionality for fetching all collections (accounts)
 * within an organization. It follows service-oriented architecture principles
 * to centralize business logic and provide a clean interface for React components.
 * 
 * The service handles the complex logic of:
 * - Permission-based collection fetching
 * - Fetching site counts for each collection
 * - Supporting both admin and regular user access patterns
 */
export const organizationCollectionService = {
  
  /**
   * Get all collections for an organization with proper permission handling
   * 
   * This function handles different access patterns:
   * - KWALLAdmin users: Can see all collections in organization
   * - Regular users: Can only see collections they have access to
   * 
   * @param {string} organizationId - The organization ID to get collections for
   * @param {string} userId - Current user ID for permission checking
   * @param {string} userRole - User role for admin access checking
   * @returns {Promise<Array>} - Array of collection objects
   */
  async getCollectionsForOrganization(organizationId, userId, userRole) {
    try {
      // Debug logging for organization htTI3QubGSTOWsftj6i5
      const isDebugOrg = organizationId === 'htTI3QubGSTOWsftj6i5';
      if (isDebugOrg) {
        console.log('🔍 [DEBUG] Starting getCollectionsForOrganization for', { organizationId, userId, userRole });
      }
      
      // Validate input parameters
      if (!organizationId || !userId) {
        console.warn("Missing required parameters for getCollectionsForOrganization", { organizationId, userId });
        return [];
      }

      // Get the organization data to access the accounts array
      const orgRef = doc(Firestore, "organizations", organizationId);
      const orgSnapshot = await getDoc(orgRef);
      
      if (!orgSnapshot.exists()) {
        console.warn("Organization not found:", organizationId);
        return [];
      }
      
      const currentOrganization = orgSnapshot.data();
      
      if (isDebugOrg) {
        console.log('🔍 [DEBUG] Organization data:', {
          name: currentOrganization.name,
          accounts: currentOrganization.accounts,
          accountsLength: currentOrganization.accounts?.length,
          admins: currentOrganization.admins
        });
      }
      
      const records = [];

      // Check if user is kwallAdmin
      const isUserKwallAdmin = isKWALLAdmin(userRole);
      
      if (isDebugOrg) {
        console.log('🔍 [DEBUG] Permission check:', { isUserKwallAdmin, userRole });
      }

      // If user is kwallAdmin and there's an active organization, show ALL collections of that org
      if (isUserKwallAdmin && currentOrganization?.accounts) {
        if (isDebugOrg) {
          console.log('🔍 [DEBUG] Taking kwallAdmin path with organization accounts');
        }
        
        if (Array.isArray(currentOrganization.accounts)) {
          // Batch get all accounts from the current organization
          const batch = [];
          for (let accountId of currentOrganization.accounts) {
            if (accountId) {
              batch.push(doc(Firestore, "accounts", accountId));
            }
          }

          // Fetch all accounts in the organization
          if (batch.length > 0) {
            const accountDocs = await Promise.all(
              batch.map((docRef) => getDoc(docRef))
            );

            accountDocs.forEach((snap) => {
              if (snap.exists()) {
                const accountData = snap.data();
                if (isDebugOrg) {
                  console.log('🔍 [DEBUG] Processing account:', {
                    id: snap.id,
                    name: accountData.name,
                    status: accountData.status,
                    accountActivation: accountData.accountActivation,
                    accountType: accountData.accountType
                  });
                }
                // kwallAdmin sees all collections in org regardless of access rights
                if (!accountData.status || accountData.status !== "deleted") {
                  records.push({
                    id: snap.id,
                    name: accountData.name || 'Unnamed Account',
                    accountType: accountData.accountType,
                    subscriptionStatus: accountData.subscriptionStatus,
                  });
                  if (isDebugOrg) {
                    console.log('🔍 [DEBUG] Added account to records');
                  }
                } else {
                  if (isDebugOrg) {
                    console.log('🔍 [DEBUG] Filtered out account due to deleted status');
                  }
                }
              } else {
                if (isDebugOrg) {
                  console.log('🔍 [DEBUG] Account document does not exist');
                }
              }
            });
          }
        }
      }
      // If kwallAdmin has no active organization, show collections they have regular access to
      else if (isUserKwallAdmin && !currentOrganization?.accounts) {
        if (isDebugOrg) {
          console.log('🔍 [DEBUG] Taking kwallAdmin path without organization accounts');
        }
        const accountRef = query(
          collection(Firestore, "accounts"),
          where("accountActivation", "==", true),
          where("access", "array-contains", userId)
        );
        const accountData = await getDocs(accountRef);

        accountData.forEach((account) => {
          if (!account.data().status || account.data().status !== "deleted") {
            records.push({
              id: account.id,
              name: account.data().name || 'Unnamed Account',
              accountType: account.data().accountType,
              subscriptionStatus: account.data().subscriptionStatus,
            });
          }
        });
      }
      // If there's a current organization and user is not kwallAdmin, filter by organization
      else if (currentOrganization?.accounts) {
        if (isDebugOrg) {
          console.log('🔍 [DEBUG] Organization has accounts, checking admin status');
          console.log('🔍 [DEBUG] Current user in admins?', currentOrganization.admins?.includes(userId));
        }
        // For Admin or orgAdmin
        if (
          currentOrganization.admins?.includes(userId) &&
          Array.isArray(currentOrganization.accounts)
        ) {
          if (isDebugOrg) {
            console.log('🔍 [DEBUG] Taking organization admin path');
          }
          // Batch get accounts instead of fetching one by one
          const batch = [];
          for (let accountId of currentOrganization.accounts) {
            if (accountId) {
              batch.push(doc(Firestore, "accounts", accountId));
            }
          }

          // If there are accounts to fetch, get them in one batch
          if (batch.length > 0) {
            const accountDocs = await Promise.all(
              batch.map((docRef) => getDoc(docRef))
            );

            accountDocs.forEach((snap) => {
              if (snap.exists()) {
                const accountData = snap.data();
                records.push({
                  id: snap.id,
                  name: accountData.name || 'Unnamed Account',
                  accountType: accountData.accountType,
                  subscriptionStatus: accountData.subscriptionStatus,
                });
              }
            });
          }
        } else {
          if (isDebugOrg) {
            console.log('🔍 [DEBUG] Taking regular user path - querying accounts with access');
          }
          // For regular users, just fetch accounts they have access to
          const accountRef = query(
            collection(Firestore, "accounts"),
            where("accountActivation", "==", true),
            where("access", "array-contains", userId)
          );
          const accountData = await getDocs(accountRef);

          accountData.forEach((account) => {
            const inOrgAccounts = currentOrganization?.accounts?.includes(account.id);
            const notDeleted = (!account.data().status || account.data().status !== "deleted");
            
            if (isDebugOrg) {
              console.log('🔍 [DEBUG] Regular user account check:', {
                accountId: account.id,
                name: account.data().name,
                inOrgAccounts,
                notDeleted,
                status: account.data().status,
                accountActivation: account.data().accountActivation
              });
            }
            
            if (inOrgAccounts && notDeleted) {
              records.push({
                id: account.id,
                name: account.data().name || 'Unnamed Account',
                accountType: account.data().accountType,
                subscriptionStatus: account.data().subscriptionStatus,
              });
              if (isDebugOrg) {
                console.log('🔍 [DEBUG] Added regular user account to records');
              }
            }
          });
        }
      }

      if (isDebugOrg) {
        console.log('🔍 [DEBUG] Final records count:', records.length);
        console.log('🔍 [DEBUG] Final records:', records);
      }
      
      return records;
      
    } catch (error) {
      console.error(`Error fetching collections for organization ${organizationId}:`, error);
      // Return empty array as safe default
      return [];
    }
  },

  /**
   * Get site counts for multiple collections in a batch operation
   * 
   * This function optimizes performance by fetching site counts for multiple collections
   * concurrently using Promise.all.
   * 
   * @param {string[]} accountIds - Array of account/collection IDs
   * @returns {Promise<Object>} - Object mapping accountId to site count {accountId: count}
   */
  async batchGetCollectionSiteCounts(accountIds) {
    // Validate input
    if (!accountIds || accountIds.length === 0) {
      return {};
    }
    
    try {
      // Execute all site count requests concurrently for better performance
      const counts = await Promise.all(
        accountIds.map(async (accountId) => {
          try {
            const sitesRef = collection(Firestore, "accounts", accountId, "sites");
            const sitesSnapshot = await getDocs(sitesRef);
            return sitesSnapshot.size; // Return count of documents
          } catch (error) {
            console.error(`Error fetching site count for account ${accountId}:`, error);
            return 0; // Return 0 for failed requests to not break the batch
          }
        })
      );
      
      // Transform parallel results array into a lookup object
      // Creates mapping: {accountId1: count1, accountId2: count2, ...}
      return Object.fromEntries(accountIds.map((id, i) => [id, counts[i]]));
      
    } catch (error) {
      // Log error for debugging but return safe default object
      console.error("Error batch fetching collection site counts:", error);
      return {};
    }
  },

  /**
   * Get collections with site counts for display
   * 
   * This function combines collection data with site counts for efficient display
   * 
   * @param {string} organizationId - The organization ID
   * @param {string} userId - Current user ID
   * @param {string} userRole - User role
   * @returns {Promise<Array>} - Array of collections with site counts
   */
  async getCollectionsWithSiteCounts(organizationId, userId, userRole) {
    try {
      // First get all collections
      const collections = await this.getCollectionsForOrganization(organizationId, userId, userRole);
      
      if (collections.length === 0) {
        return [];
      }

      // Then get site counts for all collections
      const accountIds = collections.map(collection => collection.id);
      const siteCounts = await this.batchGetCollectionSiteCounts(accountIds);

      // Combine collections with their site counts
      return collections.map(collection => ({
        ...collection,
        siteCount: siteCounts[collection.id] || 0
      }));
      
    } catch (error) {
      console.error(`Error fetching collections with site counts for organization ${organizationId}:`, error);
      return [];
    }
  }
};